import { Box, Stack, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import { Link as RouterLink } from 'react-router-dom'
import { buildBreakdownSegments } from '@/lib/meterCategories'

const formatKwh = (kwh) => `${Math.round(kwh).toLocaleString('en-GB')} kWh`

/**
 * A property's electricity split by meter category (openspec: meter-categories): a
 * stacked bar with labelled shares, or a "check meter wiring" note in place of the
 * shares when sub-meters are not wired under their main incomer (the sums would
 * double count). Generation is listed apart, never part of the bar. Renders nothing
 * for a `null` breakdown, so an uncategorised org sees no change.
 */
export default function CategoryBreakdownBar({ breakdown, wiringHref }) {
    const result = buildBreakdownSegments(breakdown)
    if (!result) return null
    const { segments, generation, showShares, total } = result

    if (segments.length === 0 && generation === null) {
        return (
            <Typography variant="body2" color="text.secondary">
                No electricity recorded for this period.
            </Typography>
        )
    }

    return (
        <Stack spacing={1.5} data-testid="category-breakdown">
            {showShares && total > 0 ? (
                <Box
                    role="img"
                    aria-label={segments
                        .map(
                            (segment) => `${segment.label} ${segment.sharePct}%`
                        )
                        .join(', ')}
                    sx={{
                        display: 'flex',
                        height: 12,
                        borderRadius: 1,
                        overflow: 'hidden',
                        bgcolor: 'action.hover'
                    }}
                >
                    {segments.map((segment) => (
                        <Box
                            key={segment.category || 'uncategorised'}
                            sx={{
                                width: `${(100 * segment.kwh) / total}%`,
                                bgcolor: segment.color
                            }}
                        />
                    ))}
                </Box>
            ) : null}

            {!showShares ? (
                <Typography variant="body2" color="warning.main">
                    Check meter wiring: some sub-meters at this property have no
                    parent meter, so category totals may double count. Set each
                    sub-meter's Parent Meter to the main incomer
                    {wiringHref ? (
                        <>
                            {' '}
                            on the{' '}
                            <RouterLink to={wiringHref}>meters page</RouterLink>
                        </>
                    ) : null}
                    .
                </Typography>
            ) : null}

            <Stack
                component="ul"
                spacing={0.5}
                sx={{ m: 0, p: 0, listStyle: 'none' }}
            >
                {segments.map((segment) => (
                    <Stack
                        component="li"
                        key={segment.category || 'uncategorised'}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                    >
                        <Box
                            aria-hidden="true"
                            sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '2px',
                                bgcolor: segment.color,
                                flexShrink: 0
                            }}
                        />
                        <Typography variant="body2" sx={{ flexGrow: 1 }}>
                            {segment.label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {showShares
                                ? `${segment.sharePct}% · ${formatKwh(segment.kwh)}`
                                : formatKwh(segment.kwh)}
                        </Typography>
                    </Stack>
                ))}
                {generation !== null ? (
                    <Stack
                        component="li"
                        direction="row"
                        spacing={1}
                        alignItems="center"
                    >
                        <Typography variant="body2" sx={{ flexGrow: 1 }}>
                            Generation (not counted as consumption)
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {formatKwh(generation)}
                        </Typography>
                    </Stack>
                ) : null}
            </Stack>
        </Stack>
    )
}

CategoryBreakdownBar.propTypes = {
    breakdown: PropTypes.shape({
        buckets: PropTypes.array,
        consumption_kwh: PropTypes.number,
        generation_kwh: PropTypes.number,
        untreed_sub_meters: PropTypes.bool
    }),
    wiringHref: PropTypes.string
}
